/**
 * Custom JavaScript utilities for the Shopify theme.
 * Provides functionality for:
 * - Quantity input validation
 * - Payroll deduction calculations
 * - Cart operations and store ID management
 * - Collection filter toggling
 * - Algolia promo block positioning
 * - Query parameter manipulation
 * - Cart management (add/change items)
 * - Browser detection
 * 
 * @requires jQuery (for some functions)
 * @requires Shopify (global)
 */

// ============================================
// Quantity Input Validation
// ============================================

if (document.querySelector('.quantity-wrapper input[name="quantity"]')) {
  document.querySelectorAll('.quantity-wrapper input[name="quantity"]').forEach(function (el) {
    el.addEventListener("change", (e) => {
      let max = e.target.getAttribute('max');
      // Clamp quantity to maximum allowed value
      if (parseInt(e.target.value) > parseInt(max)) {
        e.target.value = max;
      }
    });
  });
}


// ============================================
// Payroll Deduction UI Handlers
// ============================================

if (document.querySelector(".payroll-deduction-container .payroll-deduction-open")) {
  document.querySelectorAll(".payroll-deduction-container .payroll-deduction-open").forEach(function (el) {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      let cont = el.closest('.payroll-deduction-container');
      cont.classList.add('active');
    });
  });
}

if (document.querySelector(".payroll-deduction-container .payroll-deduction-close")) {
  document.querySelectorAll(".payroll-deduction-container .payroll-deduction-close").forEach(function (el) {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      let cont = el.closest('.payroll-deduction-container');
      cont.classList.remove('active');
    });
  });
}


// ============================================
// Variant Change Event Dispatcher
// ============================================

/**
 * Dispatches custom event when a non-swatch variant option is clicked.
 * Allows other components to react to variant changes.
 * 
 * @listens click - On .js-option:not(.swatch) elements
 * @event theme:variantchanged - Custom event with variant ID detail
 */
document.querySelectorAll('.js-option:not(.swatch)').forEach(function (el) {
  el.addEventListener("click", (e) => {
    document.dispatchEvent(new CustomEvent('theme:variantchanged', {detail: {id: parseInt(document.querySelector('[name="id"]').value)}}))
  })
})


// ============================================
// Cart Change Handler
// ============================================

/**
 * Listens for cart changes and reloads payroll deduction calculations.
 * 
 * @listens theme:cartchanged - Custom event dispatched when cart updates
 * @param {CustomEvent} e - Event containing cart data
 */
document.addEventListener('theme:cartchanged', (e) => {
  handleCartChanged(e);
})

/**
 * Fetches updated cart data and triggers payroll deduction reload.
 * 
 * @function handleCartChanged
 * @param {CustomEvent} e - Cart change event
 * @returns {void}
 */
function handleCartChanged(e) {
  fetch('/cart.js')
    .then(res => res.json())
    .then(response => {
      reloadPayrollDeduction(response);
    });
}

/**
 * Reloads payroll deduction display based on cart total.
 * Calculates installment amounts and updates the UI with payment periods.
 * Shows/hides the payroll deduction container based on cart total limits.
 * 
 * @function reloadPayrollDeduction
 * @param {Object} response - Cart object from /cart.js
 * @returns {void}
 * @sideEffect Modifies DOM elements to show/hide payroll deduction UI
 * 
 * @example
 * reloadPayrollDeduction({ original_total_price: 5000 });
 */
function reloadPayrollDeduction(response) {
  let cont = document.querySelector('.payroll-deduction-container');
  if (!cont || !response) return;
  
  let warning = cont.querySelector('.warning');
  let content = cont.querySelector('.content');
  let minTotal = parseInt(cont.dataset.minTotal);
  let maxTotal = parseInt(cont.dataset.maxTotal);
  let bands = cont.dataset.bands;
  
  // Parse installment bands from data attribute
  if (bands) {
    bands = JSON.parse(bands);
  }

  let total = response.original_total_price;

  // Show/hide container based on cart total limits
  if (total > maxTotal || total < minTotal) {
    warning.classList.remove('hidden');
    cont.classList.add('hidden');
    content.classList.add('hidden');
  } else {
    warning.classList.add('hidden');
    cont.classList.remove('hidden');
    content.classList.remove('hidden');
  }

  // Determine which payment band applies
  let currentBand = null;
  bands.forEach((band) => {
    let bandTotal = band.maximum_total * 100

    if (!currentBand && bandTotal > total) {
      currentBand = band;
    }
  });

  let monthly = 0;
  let remainder = 0;
  let installments = 0;

  // Calculate installment amounts
  if (currentBand) {
    installments = currentBand.installments_count;
    monthly = Math.floor(total / installments);
    let calcTotal = monthly * installments;

    if (calcTotal < total) {
      remainder = total - calcTotal;
    }
  }

  // Clear band if outside limits
  if (maxTotal < total) {
    currentBand = null
  }
  if (minTotal > total) {
    currentBand = null
  }

  // Create DOM elements for period display
  let newEl = document.createElement('div');
  newEl.classList.add('period');

  let newEl2 = document.createElement('div');
  newEl2.classList.add('title');
  newEl.appendChild(newEl2);

  let newEl3 = document.createElement('div');
  newEl3.classList.add('amount');
  newEl.appendChild(newEl3);

  // Clear existing periods
  document.querySelector('.payroll-deduction-container .periods').innerHTML = '';

  // Generate installment period elements
  for (let i = 0; i < installments; i++) {
    let period = i + 1;
    let clone = newEl.cloneNode(true);
    let amount = monthly;

    // Add remainder to final payment
    if (i === (installments - 1)) {
      amount += remainder;
    }

    amount = amount / 100;

    clone.querySelector('.title').innerText = 'Pay Period ' + period;
    clone.querySelector('.amount').innerText = '$' + amount.toFixed(2);

    document.querySelector('.payroll-deduction-container .periods').appendChild(clone);
  }
}


// ============================================
// Cookie Management Utilities
// ============================================

/**
 * Sets a cookie with optional expiration.
 * 
 * @function setCookie
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} [days] - Expiration in days
 * @param {number} [minutes] - Expiration in minutes (alternative to days)
 * @returns {void}
 * 
 * @example
 * // Cookie expires in 7 days
 * setCookie('user_preference', 'dark_mode', 7);
 * 
 * // Cookie expires in 30 minutes
 * setCookie('session_token', 'abc123', null, 30);
 */
function setCookie(name, value, days = null, minutes = null) {
  let expires = "";
  if (days) {
    let date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  } else if (minutes) {
    let seconds = minutes * 60;
    expires = "; max-age=" + seconds;
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

/**
 * Retrieves a cookie value by name.
 * 
 * @function getCookie
 * @param {string} name - Cookie name to retrieve
 * @returns {string|null} Cookie value or null if not found
 * 
 * @example
 * const theme = getCookie('theme_preference');
 */
function getCookie(name) {
  var nameEQ = name + "=";
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/**
 * Deletes a cookie by setting expiration to the past.
 * 
 * @function eraseCookie
 * @param {string} name - Cookie name to delete
 * @returns {void}
 * 
 * @example
 * eraseCookie('user_session');
 */
function eraseCookie(name) {
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}


// ============================================
// Shipping Carrier Line Item Properties
// ============================================

/**
 * Keeps every cart line in sync with the hidden properties read by the custom
 * shipping carrier. Shopify sends one CarrierService request per fulfillment
 * location, so every delivery group must carry the full-cart totals and the same
 * cart session UUID.
 */
const shippingCarrierLineItemProperties = {

  bound: false,
  running: false,
  pendingForceNewUuid: false,
  pendingPromise: null,
  checkoutSelector: '.cart-form [name="checkout"], .additional-checkout-buttons input, a[href*="/checkout"]',

  log: function () {
    if (window.location.hash == "#enable-shipping-carrier-debug") {
      console.log('shippingCarrierLineItemProperties:', ...arguments);
    }
  },

  cartUrl: function (path) {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return root + path;
  },

  generateUuid: function () {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  },

  getUuid: function (forceNewUuid) {
    let uuid = sessionStorage.getItem('_cart_session_uuid');
    if (forceNewUuid || !uuid) {
      uuid = this.generateUuid();
      sessionStorage.setItem('_cart_session_uuid', uuid);
    }
    return uuid;
  },

  getStoreId: function (cartData) {
    return (cartData.attributes && cartData.attributes._store_id) ||
      window.storeId ||
      window.multistore_id ||
      null;
  },

  getExpectedProperties: function (cartData, forceNewUuid) {
    const totalPrice = cartData.items.reduce((sum, item) => {
      const linePrice = item.final_line_price ?? (item.price * item.quantity);
      return sum + linePrice;
    }, 0);
    const totalWeightGrams = cartData.items.reduce((sum, item) => sum + (item.grams * item.quantity), 0);
    const storeId = this.getStoreId(cartData);
    const uuid = this.getUuid(forceNewUuid);

    return {
      _cart_session_uuid: uuid,
      _total_price: totalPrice,
      _total_weight: totalWeightGrams,
      _cart_total_price: totalPrice,
      _cart_total_weight_grams: totalWeightGrams,
      ...(storeId ? { _store_id: storeId } : {})
    };
  },

  itemNeedsUpdate: function (item, expectedProperties) {
    const currentProperties = item.properties || {};
    return Object.keys(expectedProperties).some((key) => {
      return String(currentProperties[key] ?? '') !== String(expectedProperties[key]);
    });
  },

  updateItem: function (item, expectedProperties) {
    return fetch(this.cartUrl('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: {
          ...(item.properties || {}),
          ...expectedProperties
        }
      })
    }).then((res) => res.json());
  },

  run: function (options = {}) {
    if (options.forceNewUuid) {
      this.pendingForceNewUuid = true;
    }

    if (this.running) {
      return this.pendingPromise || Promise.resolve();
    }

    this.running = true;
    const forceNewUuid = this.pendingForceNewUuid;
    this.pendingForceNewUuid = false;

    this.pendingPromise = fetch(this.cartUrl('cart.js'))
      .then((res) => res.json())
      .then((cartData) => {
        if (!cartData.items || !cartData.items.length) {
          return null;
        }

        const expectedProperties = this.getExpectedProperties(cartData, forceNewUuid);
        const itemsToUpdate = cartData.items.filter((item) => this.itemNeedsUpdate(item, expectedProperties));

        this.log('items to update:', itemsToUpdate.length, expectedProperties);

        return itemsToUpdate.reduce((promise, item) => {
          return promise.then(() => this.updateItem(item, expectedProperties));
        }, Promise.resolve());
      })
      .catch((error) => {
        this.log('error:', error);
      })
      .then(() => {
        this.running = false;
        this.pendingPromise = null;

        if (this.pendingForceNewUuid) {
          return this.run();
        }

        return null;
      });

    return this.pendingPromise;
  },

  shouldForceNewUuidOnLoad: function () {
    return document.body && document.body.classList.contains('template-cart');
  },

  handleCheckout: function (event) {
    if (event.defaultPrevented) {
      return;
    }

    const target = event.target && event.target.closest
      ? event.target.closest(this.checkoutSelector)
      : null;

    if (!target || target.dataset.shippingCarrierPropertiesReady === 'true') {
      return;
    }

    event.preventDefault();
    target.dataset.shippingCarrierPropertiesReady = 'true';

    this.run().then(() => {
      if (target.tagName === 'A' && target.href) {
        window.location.href = target.href;
        return;
      }

      const form = target.closest('form');
      if (form) {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(target);
        } else {
          form.submit();
        }
      } else {
        target.click();
      }
    });
  },

  init: function () {
    if (this.bound) return;
    this.bound = true;

    document.addEventListener("cart:ready", () => this.run());
    document.addEventListener("cart:updated", () => this.run());
    document.addEventListener('theme:cartchanged', () => this.run());
    document.addEventListener('click', (event) => this.handleCheckout(event));

    this.run({ forceNewUuid: this.shouldForceNewUuidOnLoad() });
  }
};

document.addEventListener('DOMContentLoaded', () => shippingCarrierLineItemProperties.init());


// ============================================
// Collection Filter Toggle
// ============================================

if (document.querySelector('.collection-filter-btn')) {
  document.querySelector('.collection-filter-btn').addEventListener('click', (e) => {
    toggleCollectionFilters();
  });
}

if (document.querySelector('.collection-filter-cc-popup')) {
  document.querySelector('.collection-filter-cc-popup').addEventListener('click', (e) => {
    toggleCollectionFilters();
  });
}

/**
 * Toggles the collection filter popup visibility.
 * Manages body classes for scroll prevention and popup visibility.
 * 
 * @function toggleCollectionFilters
 * @returns {void}
 * @sideEffect Toggles CSS classes on body and popup element
 * 
 * @example
 * toggleCollectionFilters();
 */
function toggleCollectionFilters() {
  document.querySelector('body').classList.toggle('filters-open');
  document.querySelector('body').classList.toggle('cc-popup-no-scroll');
  document.querySelector('body').classList.toggle('cc-popup-no-scroll-pad-right');
  document.querySelector('.collection-filter-cc-popup').classList.toggle('cc-popup--visible');
}


// ============================================
// Algolia Promo Block Setup
// ============================================

/**
 * MutationObserver that watches for Algolia collection page DOM changes.
 * Sets up promo block positioning when Algolia results load.
 * 
 * @description Monitors the main container for childList mutations,
 *              then calls setUpAlgoliaPromoBlock() to position promo content.
 */
const algoliaPromoBlockObserver = new MutationObserver(mutationList =>
  mutationList.filter(m => m.type === 'childList').forEach(m => {
    setUpAlgoliaPromoBlock();
    m.addedNodes.forEach((e) => {
      // Node added - could be used for additional processing
    });
  }));

// Start observing if container exists
if (document.querySelector('.template-collection:not(.algolia-plp-block-added) .main-container')) {
  algoliaPromoBlockObserver.observe(document.querySelector('.template-collection:not(.algolia-plp-block-added) .main-container'), {
    childList: true,
    subtree: true
  });
}

/**
 * Moves and configures the Algolia promo block within search results.
 * Clones the promo element, adds required image/text, and positions in results list.
 * 
 * @function setUpAlgoliaPromoBlock
 * @returns {void}
 * @sideEffect Modifies DOM to move and configure promo block
 * 
 * @example
 * setUpAlgoliaPromoBlock();
 */
function setUpAlgoliaPromoBlock() {
  // Check if promo block exists and hasn't been moved yet
  if (document.querySelector('.algolia-plp-promo-block') && !document.querySelector('.algolia-plp-promo-block').classList.contains('algolia-plp-block-moved')) {
    let _target = document.querySelector('.algolia-plp-promo-block');
    let target = _target.cloneNode();
    let parent = _target.closest('.ais-Hits-list');
    let newLi = document.createElement('li');

    newLi.classList.add('ais-Hits-item');
    target.classList.add('algolia-plp-block-moved');

    // Add image if not present
    if (!target.querySelector('img')) {
      let newImg = document.createElement('img');
      newImg.setAttribute('src', target.dataset.image);
      target.appendChild(newImg);
    }
    
    // Add text if not present
    if (!target.querySelector('span')) {
      let newText = document.createElement('span');
      newText.innerText = target.dataset.title;
      target.appendChild(newText);
    }

    // Position at top of results
    newLi.appendChild(target);
    parent.prepend(newLi);

    // Mark as processed
    document.querySelector('body').classList.add('algolia-plp-block-added');

    // Remove original element
    _target.remove();
  }
}


// ============================================
// URL Query Parameter Utilities
// ============================================

/**
 * Replaces or adds a query parameter in a URL search string.
 * 
 * @function replaceQueryParam
 * @param {string} param - Parameter name to replace
 * @param {string} newval - New parameter value (empty string to remove)
 * @param {string} search - Current URL search string (including ?)
 * @returns {string} Updated search string
 * 
 * @example
 * replaceQueryParam('page', '2', '?sort=price');
 * // Returns "?sort=price&page=2"
 */
function replaceQueryParam(param, newval, search) {
  var regex = new RegExp("([?;&])" + param + "[^&;]*[;&]?");
  var query = search.replace(regex, "$1").replace(/&$/, '');

  return (query.length > 2 ? query + "&" : "?") + (newval ? param + "=" + newval : '');
}

/**
 * Extracts a query parameter value from URL search string.
 * 
 * @function getQueryParam
 * @param {string} param - Parameter name to retrieve
 * @param {string} search - URL search string
 * @returns {string|null} Parameter value or null if not found
 * 
 * @example
 * getQueryParam('q', window.location.search);
 */
function getQueryParam(param, search) {
  let url = new URL(search);
  return url.searchParams.get(param);
}


// ============================================
// Input Character Limit Validation
// ============================================

/**
 * Validates input fields against their character limit data attribute.
 * Currently reads but doesn't enforce limits.
 * 
 * @function validateInputCharLimits
 * @returns {void}
 */
function validateInputCharLimits() {
  if (document.querySelector('input[type="text"][data-character-limit]')) {
    document.querySelectorAll('input[type="text"][data-character-limit]').forEach(el => {
      let val = el.value;
      let length = val.length;
      let charLimit = el.dataset.characterLimit;
      if (charLimit) charLimit = parseInt(charLimit);
    });
  }
}

// Run validation on load
validateInputCharLimits();


// ============================================
// Cart Operations
// ============================================

/**
 * Adds an item to the cart with optional extra properties.
 * 
 * @function addItemToCart
 * @param {Object} item - Cart item object (must include id)
 * @param {Object} [extras={}] - Additional properties to merge
 * @returns {Promise<Object>} Resolution object with status, message, and cart data
 * @rejects {Object} Rejection with status, message, and variant_id
 * 
 * @example
 * const result = await addItemToCart({ id: 123456789, quantity: 1 });
 * console.log(result.cart);
 */
function addItemToCart(item, extras = {}) {

  return new Promise(async (resolve, reject) => {

    const cartResponse = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({...item, ...extras})
    });

    if (cartResponse.ok) {
      resolve({
        status: 200,
        message: 'Item added to cart.',
        cart: await cartResponse.json()
      });
      return;
    }

    reject({
      status: cartResponse.status,
      message: cartResponse.statusText,
      variant_id: item.id,
    });
  });
}

/**
 * Changes a cart item's quantity or properties.
 * 
 * @function changeCartItem
 * @param {Object} data - Cart change data (id, quantity, properties)
 * @param {Object} [extras={}] - Additional properties to merge
 * @returns {Promise<Object>} Resolution object with status, message, and cart data
 * @rejects {Object} Rejection with status, message, and data
 * 
 * @example
 * const result = await changeCartItem({ id: 'abc123', quantity: 2 });
 */
function changeCartItem(data, extras = {}) {

  return new Promise(async (resolve, reject) => {

    const cartResponse = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({...data, ...extras})
    });

    if (cartResponse.ok) {
      resolve({
        status: 200,
        message: `Item updated - ${data.id}.`,
        cart: await cartResponse.json()
      });
      return;
    }

    reject({
      status: cartResponse.status,
      message: cartResponse.statusText,
      data: data,
    });
  });
}


// ============================================
// Browser Detection
// ============================================

/**
 * Detects the current browser from User-Agent string.
 * 
 * @function getBrowser
 * @returns {string} Browser name: 'Opera', 'Edge', 'Chrome', 'Safari', 'Firefox', 'IE', or 'unknown'
 * 
 * @example
 * const browser = getBrowser();
 * if (browser === 'Safari') { ... }
 */
function getBrowser() {
  if ((navigator.userAgent.indexOf("Opera") || navigator.userAgent.indexOf('OPR')) != -1) {
    return 'Opera';
  } else if (navigator.userAgent.indexOf("Edg") != -1) {
    return 'Edge';
  } else if (navigator.userAgent.indexOf("Chrome") != -1) {
    return 'Chrome';
  } else if (navigator.userAgent.indexOf("Safari") != -1) {
    return 'Safari';
  } else if (navigator.userAgent.indexOf("Firefox") != -1) {
    return 'Firefox';
  } else if ((navigator.userAgent.indexOf("MSIE") != -1) || (!!document.documentMode == true)) //IF IE > 10
  {
    return 'IE';
  } else {
    return 'unknown';
  }
}


// ============================================
// External Link Handler
// ============================================

// Open outside links in new tab with security attributes
var links = document.links;
for (let i = 0, linksLength = links.length; i < linksLength; i++) {
  if (links[i].hostname !== window.location.hostname) {
    links[i].target = '_blank';
    links[i].rel = 'noreferrer noopener';
  }
}
